import {
  $createListNode,
  $isListItemNode,
  $isListNode,
  ListItemNode,
  ListNode,
} from '@lexical/list';
import { $findMatchingParent } from '@lexical/utils';
import {
  $createLineBreakNode,
  $getSelection,
  $insertNodes,
  $isLineBreakNode,
  $isParagraphNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $isTextNode,
  LexicalNode,
} from 'lexical';
import { $isImageNode, ImageNode } from '../nodes/ImageNode';

export function $getSelectedListItem(): ListItemNode | null {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return null;
  return $findMatchingParent(
    selection.anchor.getNode(),
    $isListItemNode,
  ) as ListItemNode | null;
}

/**
 * 이미지를 목록 항목 안에 넣는다(이미지는 inline 이어야 함).
 * 블록으로 넣으면 목록이 둘로 끊겨 뒤 번호가 1부터 다시 시작됨.
 * 빈 항목에서 넣으면 바로 위 항목 아래로 붙여 번호를 차지하지 않게 한다.
 */
export function $insertImageInListItem(
  listItem: ListItemNode,
  image: LexicalNode,
): void {
  const prev = listItem.getPreviousSibling();
  if (
    listItem.isEmpty() &&
    $isListItemNode(prev) &&
    !$isListNode(prev.getFirstChild())
  ) {
    listItem.remove();
    prev.append($createLineBreakNode(), image);
    return;
  }

  $insertNodes([image]);
  const before = image.getPreviousSibling();
  if (before && !$isLineBreakNode(before)) {
    image.insertBefore($createLineBreakNode());
  }
  const after = image.getNextSibling();
  if (after && !$isLineBreakNode(after) && after.getTextContent() !== '') {
    image.insertAfter($createLineBreakNode());
  }
}

/** 붙여넣은 내용이 이미지만으로 이뤄졌으면 그 이미지들, 아니면 null */
export function $getPastedImagesOnly(nodes: LexicalNode[]): ImageNode[] | null {
  const images: ImageNode[] = [];
  const isImageOnly = (node: LexicalNode): boolean => {
    if ($isImageNode(node)) {
      images.push(node);
      return true;
    }
    if ($isLineBreakNode(node)) return true;
    if ($isTextNode(node)) return node.getTextContent().trim() === '';
    if ($isParagraphNode(node)) return node.getChildren().every(isImageOnly);
    return false;
  };
  return nodes.every(isImageOnly) && images.length > 0 ? images : null;
}

/** 이미지가 목록 밖 한 줄을 혼자 차지하고 있으면 그 줄(이미지 또는 이미지만 든 문단) */
function $getStandaloneImageBlock(image: ImageNode): LexicalNode | null {
  const parent = image.getParent();
  if ($isRootOrShadowRoot(parent)) return image;
  if (
    $isParagraphNode(parent) &&
    $isRootOrShadowRoot(parent.getParent()) &&
    $getPastedImagesOnly(parent.getChildren())?.length === 1
  ) {
    return parent;
  }
  return null;
}

/**
 * Tab: 목록 바로 아래 따로 있는 이미지를 위 목록의 마지막 항목 안으로 넣고,
 * 아래에 같은 종류 목록이 이어지면 합쳐 번호가 이어지게 한다.
 */
export function $moveImageIntoListAbove(image: ImageNode): boolean {
  const block = $getStandaloneImageBlock(image);
  const list = block?.getPreviousSibling();
  if (!block || !$isListNode(list)) return false;

  let target = list.getLastChild();
  while ($isListItemNode(target) && $isListNode(target.getFirstChild())) {
    target = (target.getFirstChild() as ListNode).getLastChild();
  }
  if (!$isListItemNode(target)) return false;

  const nextList = block.getNextSibling();
  image.setInline(true);
  target.append($createLineBreakNode(), image);
  if (block !== image) block.remove();

  if ($isListNode(nextList) && nextList.getListType() === list.getListType()) {
    const first = nextList.getFirstChild();
    // 예전에 이미지 때문에 생긴 빈 번호 줄은 합치면서 지움
    if ($isListItemNode(first) && first.isEmpty()) first.remove();
    list.append(...nextList.getChildren());
    nextList.remove();
  }
  return true;
}

/**
 * Shift+Tab(맨 위 단계 항목): 항목 안 이미지를 목록 밖으로 꺼내 항목 바로 아래 한 줄로 둔다.
 * 목록은 이미지 위아래로 나뉜다.
 */
export function $moveImageOutOfList(image: ImageNode): boolean {
  const listItem = image.getParent();
  const list = listItem?.getParent();
  if (!$isListItemNode(listItem) || !$isListNode(list)) return false;
  if ($isListItemNode(list.getParent())) return false;

  const before = image.getPreviousSibling();
  const after = image.getNextSibling();
  // 이미지 앞뒤 글이 한 줄로 붙지 않게 줄바꿈은 하나만 지움
  if ($isLineBreakNode(before) && (!after || $isLineBreakNode(after))) {
    before.remove();
  } else if (!before && $isLineBreakNode(after)) {
    after.remove();
  }

  const restItems = listItem.getNextSiblings();
  image.setInline(false);
  list.insertAfter(image);
  if (restItems.length > 0) {
    const restList = $createListNode(list.getListType());
    restList.append(...restItems);
    image.insertAfter(restList);
  }
  if (listItem.isEmpty()) listItem.remove();
  if (list.isEmpty()) list.remove();
  return true;
}
